import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAssessment } from "@/context/AssessmentContext";
import { API } from "@/lib/api";

export default function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm your Apexture physio assistant. Ask me about form, pain modifications, or your rehab plan." }
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sidRef = useRef(`chat_${Math.random().toString(36).slice(2, 10)}`);
  const scrollRef = useRef(null);
  const { data } = useAssessment() || {};

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          session_id: sidRef.current,
          message: text,
          context: data,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Sorry, I couldn't reach the assistant. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-forest text-bone shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
        aria-label="Open chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-50 w-[90vw] max-w-[380px] h-[520px] bg-white border border-forest/15 rounded-xl shadow-2xl flex flex-col animate-fade-in"
        >
          <div className="px-4 py-3 border-b border-forest/10 bg-forest text-bone rounded-t-xl">
            <p className="font-heading font-semibold">Physio Assistant</p>
            <p className="text-xs opacity-80">Evidence-based guidance · Not medical advice</p>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 thin-scroll">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "bg-forest text-bone" : "bg-bone border border-forest/10 text-ink"
                  }`}
                >
                  {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-forest/10 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about form, pain, exercise…"
              className="flex-1 px-3 py-2 rounded-md border border-forest/20 focus:border-forest outline-none text-sm"
              data-testid="chat-input"
            />
            <Button onClick={send} disabled={busy} size="sm" className="bg-forest hover:bg-forest/90 text-bone" data-testid="chat-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
